/**
 * Auth API — the two-call login the legacy app performs.
 *
 *   1. POST {VITE_API_BASE_URL}/login        -> { jwtToken, ErrorCode, Message }
 *   2. POST {VITE_CS_API}/getUserToken       -> { Data: { UserData, FeaturesData } }
 *
 * They are separate endpoints rather than one, because they hit two different
 * backends with two different auth conventions, and the hook has to inspect the
 * first result before it is allowed to make the second call.
 *
 * Ported from Login.vue:127-226 / apiservice.js:338-349. Two legacy behaviours
 * are deliberately NOT reproduced; both are in useLogin.js where the sequencing
 * lives:
 *  - legacy calls getUserToken BEFORE testing the login result, so a wrong
 *    password sends `accessToken: Bearer undefined` and throws;
 *  - that throw lands in `catch (e) { }` — an empty catch — so a failed login
 *    shows the user nothing at all.
 */
import { env } from '@config/env';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { baseApi } from '@services/baseApi';

const { LOGIN, GET_USER_TOKEN } = API_ENDPOINTS.AUTH;

/**
 * The backend's own failure signal, which arrives on an HTTP 200.
 *
 * `ErrorCode: -1` with a human `Message` is how /login reports bad credentials
 * (Login.vue:145). Because it is not an HTTP error, no interceptor sees it —
 * every caller has to check explicitly or treat a rejection as success.
 */
export const LOGIN_ERROR_CODE = -1;

/**
 * Base64 the password, exactly as legacy does.
 *
 * This is NOT encryption — `btoa` is reversible by anyone. It is reproduced
 * because it is the wire format the existing /login endpoint expects, not
 * because it protects anything. The legacy variable is named `encryptPassword`
 * (Login.vue:130), which is how the misunderstanding spread.
 */
export function encodePassword(password) {
  return btoa(password);
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Credentials -> jwtToken. Sent with no Authorization header, matching
     * legacy's dedicated `baseURL_WitoutAuth` instance; apiClient simply has no
     * token in the store at this point, so none is attached.
     */
    login: builder.mutation({
      query: ({ email, password }) => ({
        url: LOGIN,
        method: 'POST',
        body: { email, password: encodePassword(password) },
      }),
    }),

    /**
     * jwtToken -> identity + per-product roles.
     *
     * Two things here are unlike every other call in this app:
     *  - it lives on the Common Services host, not the advisory API, so the URL
     *    is absolute (axios ignores baseURL when given one);
     *  - it authenticates with a header literally named `accessToken`, NOT
     *    `Authorization` (Login.vue:214). A client that standardises on
     *    Authorization gets rejected here.
     *
     * The token is passed in rather than read from the store because at this
     * point in the sequence it has not been committed yet — if getUserToken
     * fails there should be no half-signed-in state left behind.
     */
    getUserToken: builder.mutation({
      query: ({ jwtToken, application }) => ({
        url: `${env.csApiBaseUrl}${GET_USER_TOKEN}`,
        method: 'POST',
        body: { application },
        headers: { accessToken: `Bearer ${jwtToken}` },
      }),
      transformResponse: (response) => response?.Data ?? null,
    }),
  }),
});

export const { useLoginMutation, useGetUserTokenMutation } = authApi;
